import { Injectable, computed, signal } from '@angular/core';
import { MessageService } from 'primeng/api';
import localForage from 'localforage';

@Injectable({
  providedIn: 'root'
})
export class AppNotificationService {
  private store: LocalForage;

  private _msgs = signal<any[]>([]);
  public readonly msgs = computed(() => this._msgs());

  public latestMessage = signal<any>(null);

  constructor(private messageService: MessageService) {
    this.store = localForage.createInstance({
      name: 'app-db',
      storeName: 'messages',
      driver: localForage.INDEXEDDB
    });
    this.loadMessages();
  }

  showSuccess(message: string): void {
    this.showMessage({ severity: 'success', summary: message });
  }

  showError(message: string): void {
    this.showMessage({ severity: 'error', summary: message });
  }

  showInfo(message: string): void {
    this.showMessage({ severity: 'info', summary: message });
  }

  showWarning(message: string): void {
    this.showMessage({ severity: 'warn', summary: message });
  }

  clearMessages(): void {
    this._msgs.set([]);
    this.store.clear();
  }

  removeMessage(message: any): void {
    const filtered = this._msgs().filter(m => m.id !== message.id);
    this._msgs.set(filtered);
    this.saveMessages();
  }

  markRead(): void {
    const updated = this._msgs().map(m => ({
      ...m,
      data: { ...(m.data || {}), read: true }
    }));
    this._msgs.set(updated);
    this.saveMessages();
  }

  showMessage(msg: any): void {
    msg.id = msg.id || this.generateUniqueId();
    msg.data = { time: new Date().getTime(), read: false };
    this.messageService.add(msg);

    const updated = [msg, ...this._msgs()];
    if (updated.length > 100) {
      updated.pop();
    }

    this._msgs.set(updated);
    this.latestMessage.set(msg);
    this.saveMessages();
  }

  private async loadMessages(): Promise<void> {
    const keys = await this.store.keys();
    const messages = await Promise.all(keys.map(key => this.store.getItem<any>(key)));
    this._msgs.set(messages);
  }

  private async saveMessages(): Promise<void> {
    await this.store.clear();
    const all = this._msgs();
    await Promise.all(all.map(msg => this.store.setItem(msg.id, msg)));
  }

  private generateUniqueId(): string {
    return '_' + Math.random().toString(36).substr(2, 9);
  }

}
